import find from 'lodash/find';
import { ShopperAccountService } from 'services/shopperAccount.service';
import { ModalService } from 'services/modal.service';
import { ApiService } from 'services/api.service';
import { PaypalSdkService } from 'services/paypalSdk.service';
import {
    getSavedCartInfo,
    setSavedCartInfo,
    formatZipCode
} from 'services/cartUtils';

import { AddShippingAddress } from './addShippingAddress/addShippingAddress';
import { SavedShippingAddress } from './savedShippingAddress/savedShippingAddress';
import { EditAccountContactInfo } from './editAccountContactInfo/editAccountContactInfo';
import { DisplayPayPalAddress } from './displayPayPalAddress/displayPayPalAddress';

import template from './editAccountContactAndDelivery.html';
import './editAccountContactAndDelivery.scss';

export const EditAccountContactAndDelivery = angular
    .module('checkout.components.editAccountContactAndDelivery', [
        AddShippingAddress,
        SavedShippingAddress,
        EditAccountContactInfo,
        DisplayPayPalAddress,
        ShopperAccountService,
        ModalService,
        ApiService,
        PaypalSdkService
    ]).service('editAccountContactAndDeliveryService', (modalService) => {
        return {
            open: (
                shopperAccount,
                phoneNumberRequired,
                initialShippingAddress,
                paypalData,
                isShippingRequired,
                $event,
                useAddressVerification,
                useNewCarts
            ) => {
                return modalService.open({
                    $event,
                    template: `
                        <edit-account-contact-and-delivery
                            shopper-account="shopperAccount"
                            phone-number-required="phoneNumberRequired"
                            initial-shipping-address="initialShippingAddress"
                            paypal-data="paypalData"
                            is-shipping-required="isShippingRequired"
                            layout="column"
                            use-address-verification="useAddressVerification"
                            use-new-carts="useNewCarts">
                        </edit-account-contact-and-delivery>
                    `,
                    cssClass: 'edit-account-contact-and-delivery',
                    data: {
                        shopperAccount,
                        phoneNumberRequired,
                        initialShippingAddress,
                        paypalData,
                        isShippingRequired,
                        useAddressVerification,
                        useNewCarts
                    },
                    clickOutsideToClose: false
                });
            }
        };
    }).component('editAccountContactAndDelivery', {
        bindings: {
            shopperAccount: '<',
            phoneNumberRequired: '<',
            initialShippingAddress: '<',
            paypalData: '<',
            isShippingRequired: '<',
            useAddressVerification: '<',
            useNewCarts: '<'
        },
        template,
        controller: class EditAccountContactAndDeliveryController {
            constructor(
                $mdDialog,
                shopperAccountService,
                apiService,
                paypalSdkService,
                $rootScope
            ) {
                angular.extend(this, {
                    $mdDialog,
                    shopperAccountService,
                    apiService,
                    paypalSdkService,
                    $rootScope,
                    shippingAddresses: []
                });

                this.onAddressEdited = this.onAddressEdited.bind(this);
                this.onAddAddressSaved = this.onAddAddressSaved.bind(this);
                this.onAddressDeleted = this.onAddressDeleted.bind(this);
            }

            $onInit() {
                this.fetchingShippingAddresses = true;
                this.getAndSetShippingAddresses().then(() => {
                    this.selectedShippingAddress = this.getBestDefault();
                }).finally(() => {
                    this.fetchingShippingAddresses = false;
                });

                this.unbindListener = this.$rootScope.$on('editAccountContactAndDelivery:Editing', (event, data) => {
                    if (data !== 'newAddress') this.showAddAddressForm = false;
                });
            }

            $onDestroy() {
                this.unbindListener();
            }

            getBestDefault() {
                let bestDefault;
                const defaultShippingAddress = this.getDefaultShippingAddress();
                const initialShippingAddress = find(this.shippingAddresses, { id: this.initialShippingAddress });

                if (initialShippingAddress) {
                    bestDefault = initialShippingAddress.id;
                } else if (this.paypalData) {
                    bestDefault = 'PayPal';
                } else if (defaultShippingAddress) {
                    bestDefault = defaultShippingAddress.id;
                } else if (this.shippingAddresses && this.shippingAddresses.length > 0) {
                    bestDefault = this.shippingAddresses[0].id;
                }
                return bestDefault;
            }

            getDefaultShippingAddress() {
                return find(this.shippingAddresses, 'address.isDefault');
            }

            onConfirmClicked() {
                if (this.useNewCarts) {
                    this.handleConfirm();
                } else {
                    this.handleLegacyConfirm();
                }
            }

            handleLegacyConfirm() {
                this.isUpdatingCart = true;

                const selectedShippingAddress = find(this.shippingAddresses, { id: this.selectedShippingAddress });
                const applyShippingAddressToCart = this.selectedShippingAddress === 'PayPal' ?
                    this.paypalSdkService.applyPaypalAddressToCart(this.paypalData) :
                    this.shopperAccountService.applyShippingAddressToCart(selectedShippingAddress);

                applyShippingAddressToCart.then((updatedCart) => {
                    this.cart = updatedCart;
                    this.close();
                }).finally(() => {
                    this.isUpdatingCart = false;
                });
            }

            handleConfirm() {
                this.isUpdatingCart = true;
                const selectedShippingAddress = find(this.shippingAddresses, { id: this.selectedShippingAddress });
                const previousCart = getSavedCartInfo();

                if (this.selectedShippingAddress === 'PayPal') {
                    const cartAddress = {
                        address1: this.paypalData.PAYMENTREQUEST_0_SHIPTOSTREET,
                        address2: this.paypalData.PAYMENTREQUEST_0_SHIPTOSTREET2,
                        city: this.paypalData.PAYMENTREQUEST_0_SHIPTOCITY,
                        state: this.paypalData.PAYMENTREQUEST_0_SHIPTOSTATE,
                        postalCode: formatZipCode(
                            this.paypalData.PAYMENTREQUEST_0_SHIPTOZIP,
                            this.paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE
                        ),
                        country: this.paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE,
                        firstName: this.paypalData.FIRSTNAME,
                        lastName: this.paypalData.LASTNAME,
                        phoneNumber: this.paypalData.PHONENUM
                    };

                    setSavedCartInfo({
                        ...previousCart,
                        shippingAddress: cartAddress,
                        billingAddress: cartAddress,
                        postalCode: formatZipCode(cartAddress.postalCode, cartAddress.country),
                        state: cartAddress.state,
                        country: cartAddress.country,
                        selectedShippingAddressId: undefined
                    });
                } else {
                    setSavedCartInfo({
                        ...previousCart,
                        shippingAddress: {
                            firstName: selectedShippingAddress.address.firstName,
                            lastName: selectedShippingAddress.address.lastName,
                            address1: selectedShippingAddress.address.address1,
                            address2: selectedShippingAddress.address.address2,
                            city: selectedShippingAddress.address.city,
                            state: selectedShippingAddress.address.state,
                            postalCode: selectedShippingAddress.address.postalCode,
                            country: selectedShippingAddress.address.country
                        },
                        selectedShippingAddressId: selectedShippingAddress.id
                    });
                }

                this.close();
            }

            close() {
                this.$mdDialog.hide({
                    cart: this.useNewCarts ? getSavedCartInfo() : this.cart,
                    shopperAccount: this.shopperAccount
                });
            }

            onContactInfoSaved(shopperAccount, cart) {
                this.shopperAccount = shopperAccount;
                this.cart = cart;
            }

            getAndSetShippingAddresses() {
                return this.apiService.account.shippingAddress.getAll()
                    .then((shippingAddresses) => {
                        this.shippingAddresses = shippingAddresses.items;
                        return shippingAddresses;
                    });
            }

            onAddressEdited() {
                return this.getAndSetShippingAddresses();
            }

            onAddressDeleted() {
                return this.getAndSetShippingAddresses().then(() => {
                    this.selectedShippingAddress = this.getBestDefault();
                });
            }

            onAddAddressClicked() {
                this.showAddAddressForm = true;
                this.selectedShippingAddress = 'new';
                this.$rootScope.$emit('editAccountContactAndDelivery:Editing', 'newAddress');
            }

            onAddAddressSaved(address) {
                return this.getAndSetShippingAddresses().then(() => {
                    this.selectedShippingAddress = address.id;
                    this.showAddAddressForm = false;
                });
            }

            onAddAddressCancelled() {
                this.showAddAddressForm = false;
                this.selectedShippingAddress = this.getBestDefault();
            }

            shouldConfirmButtonBeDisabled() {
                return this.isUpdatingCart
                    || !this.selectedShippingAddress
                    || this.selectedShippingAddress === 'new';
            }

            shouldShowAddressDeleteButton() {
                return this.shippingAddresses && this.shippingAddresses.length > 1;
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/editAccountContactAndDelivery/editAccountContactAndDelivery.js